import React from 'react';
import { Can } from '../lib/casl/AbilityContext';
import type { Permission } from '../types/Auth';

interface PermissionGateProps {
  permission: Permission;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

/**
 * Declarative component for permission-based rendering
 * Uses CASL Can component internally for consistent permission checking
 */
export const PermissionGate: React.FC<PermissionGateProps> = ({ 
  permission, 
  children, 
  fallback = null 
}) => {
  // Convert permission string to action/subject
  const [subject, action] = permission.split(':') as [string, string];
  
  const subjectMap: Record<string, string> = {
    users: 'User',
    organizations: 'Organization',
    products: 'Product',
    quotes: 'Quote',
    policies: 'Policy',
    claims: 'Claim',
    payments: 'Payment',
    farmers: 'Farmer',
    system: 'System',
    reports: 'Report',
    ledger: 'Ledger',
    notifications: 'Notification',
    roles: 'Role',
    permissions: 'Permission',
    // Navigation permissions - keep as-is (lowercase)
    topnav: 'topnav',
    nav: 'nav',
  };

  const mappedSubject = subjectMap[subject] || subject;

  return (
    <Can I={action} a={mappedSubject} passThrough>
      {(allowed) => (allowed ? children : fallback)}
    </Can>
  );
};

/**
 * Component for multiple permission checks (AND logic)
 */
interface PermissionGateAllProps {
  permissions: Permission[];
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export const PermissionGateAll: React.FC<PermissionGateAllProps> = ({ 
  permissions, 
  children, 
  fallback = null 
}) => {
  // Nested Can components for AND logic
  let component = children;
  
  for (let i = permissions.length - 1; i >= 0; i--) {
    const permission = permissions[i];
    const [subject, action] = permission.split(':') as [string, string];
    
    const subjectMap: Record<string, string> = {
      users: 'User',
      organizations: 'Organization', 
      products: 'Product',
      quotes: 'Quote',
      policies: 'Policy',
      claims: 'Claim',
      payments: 'Payment',
      farmers: 'Farmer',
      system: 'System',
      reports: 'Report',
      ledger: 'Ledger',
      notifications: 'Notification',
      roles: 'Role',
      permissions: 'Permission',
      // Navigation permissions - keep as-is (lowercase)
      topnav: 'topnav',
      nav: 'nav',
    };

    const mappedSubject = subjectMap[subject] || subject;
    
    component = (
      <Can I={action} a={mappedSubject} passThrough>
        {(allowed) => (allowed ? component : fallback)}
      </Can>
    );
  }

  return <>{component}</>;
};

/**
 * Component for multiple permission checks (OR logic)
 */
interface PermissionGateAnyProps {
  permissions: Permission[];
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export const PermissionGateAny: React.FC<PermissionGateAnyProps> = ({ 
  permissions, 
  children, 
  fallback = null 
}) => {
  return (
    <>
      {permissions.map((permission, index) => (
        <PermissionGate 
          key={index}
          permission={permission} 
          fallback={index === permissions.length - 1 ? fallback : null}
        >
          {children}
        </PermissionGate>
      ))}
    </>
  );
}; 